import React, { createContext, useContext, useReducer } from "react";
import AuthService from "../services/AuthService";
import AdjuntosService from "../services/AdjuntosService";
import UserReducer from "../reducers/UserReducer";
import {
  SHOW_SPINNER,
  HIDE_SPINNER,
  LOGIN,
  LOGOUT,
  SET_PROPERTY_USER,
  GUARDAR_USUARIO,
  EDITAR_USUARIO,
} from "../types";
import { ModalContext } from "./ModalContext";
import CustomerService from "../services/CustomerService";
import { navigate } from "@reach/router";

const initialState = {
  user: null,
  correo: null,
  password: null,
  telefono: null,
  cuenta: null,
  direccion: null,
  spinner: false,
  legacy: null,
};

export const AuthContext = createContext(initialState);

export const UserProvider = ({ children }) => {
  const [state, dispatch] = useReducer(UserReducer, initialState);

  const { success, alert } = useContext(ModalContext);

  function signIn(email, password) {
    dispatch({ type: SHOW_SPINNER });
    AuthService.signIn(email, password).catch((error) => {
      if (error.code === "auth/user-not-found") {
        alert(
          "Lo sentimos. No encontramos una cuenta con ese correo. ¡Regístrate!"
        );
      }
      if (error.code === "auth/wrong-password") {
        alert("La contraseña es incorrecta. Por favor, intenta de nuevo");
      } else {
        alert(error.toString());
      }
      dispatch({ type: HIDE_SPINNER });
    });
  }

  function userLoggedIn() {
    dispatch({ type: SHOW_SPINNER });
    AuthService.userLoggedIn(
      (user) => {
        CustomerService.getCurrentCustomer()
          .then((res) => {
            dispatch({
              type: LOGIN,
              payload: res.data.user,
            });
            dispatch({ type: HIDE_SPINNER });
          })
          .catch((error) => {
            AuthService.signOut();
            if (error.response) {
              if (error.response.status === 428) {
                return alert(
                  "Solo puedes tener una sesión activa de TBM Certifications al mismo tiempo."
                );
              }
              if (error.response.status !== 404) {
                alert(error);
              }
            }
          });
      },
      (error) => {
        if (error) {
          alert(error);
          AuthService.signOut();
          navigate("/entrar");
        }
        dispatch({ type: HIDE_SPINNER });
      }
    );
  }

  function signOut() {
    AuthService.signOut()
      .then(() => {
        dispatch({ type: LOGOUT });
        navigate("/entrar");
      })
      .catch((error) => {
        alert(error);
      });
  }

  function signUp(nombre, apellidos, correo, password, telefono, reason) {
    dispatch({ type: SHOW_SPINNER });
    AuthService.signUp(correo, password)
      .then((user) => {
        signIn(correo, password);
        const uid = user.user.uid;
        CustomerService.signUp(nombre, apellidos, correo, telefono, uid, reason)
          .then(() => {
            signIn(correo, password);
          })
          .catch((error) => {
            dispatch({ type: HIDE_SPINNER });
            if (error.response) {
              if (error.response.status === 412) {
                return alert(
                  "Ya tienes una cuenta con nosotros. Inicia sesión."
                );
              }
            }
            alert(error);
          });
      })
      .catch((error) => {
        dispatch({ type: HIDE_SPINNER });
        if (error.code) {
          if (
            error.code === "auth/email-already-exists" ||
            error.code === "auth/email-already-in-use"
          ) {
            return alert("Ya tienes una cuenta con nosotros. Inicia sesión.");
          }
        }
        alert(error);
      });
  }

  function getUsuario() {
    CustomerService.getCurrentCustomer().then((res) => {
      const { user } = res.data;
      dispatch({ type: LOGIN, payload: user });
    });
  }

  function editarUsuario() {
    dispatch({ type: EDITAR_USUARIO });
  }

  function cancelEdit() {
    dispatch({ type: GUARDAR_USUARIO });
  }

  function setPropiedadUser(key, value) {
    if (key === "idAdjunto") {
      dispatch({ type: SET_PROPERTY_USER, payload: { key: "file", value } });
      if (!value)
        dispatch({ type: SET_PROPERTY_USER, payload: { key, value } });
    } else {
      if (key === "telefono") {
        value = String(value).replace(/\D/g, "");
        value = String(value).substring(0, 10);
      }
      dispatch({ type: SET_PROPERTY_USER, payload: { key, value } });
    }
  }

  function recoverPassword(email) {
    AuthService.recoverPassword(email)
      .then(() => {
        success("Te hemos enviado un correo para reestablecer tu contraseña.");
      })
      .catch((error) => {
        if (error.code === "auth/user-not-found") {
          CustomerService.recoverPassword(email).then(() => {
            success(
              "¡Te hemos enviado un correo para reestablecer tu contraseña!"
            );
          });
        } else {
          alert("Hubo un error al enviar el correo. Inténtalo más tarde.");
        }
      });
  }

  function updatePassword(currentPassword, newPassword, onSuccess, onError) {
    AuthService.updatePassword(currentPassword, newPassword)
      .then(() => {
        success("Tu contraseña se actualizó con éxito.");
        if (onSuccess) onSuccess();
      })
      .catch((error) => {
        alert(getPasswordErrorMessage(error));
        if (onError) onError();
      });
  }

  function getPasswordErrorMessage(error) {
    switch (error.code) {
      case "auth/wrong-password":
      case "auth/invalid-credential":
        return "Tu contraseña actual es incorrecta.";
      case "auth/weak-password":
        return "Tu nueva contraseña debe tener al menos 6 caracteres.";
      case "auth/too-many-requests":
        return "Demasiados intentos. Inténtalo más tarde.";
      case "auth/requires-recent-login":
        return "Por seguridad, vuelve a iniciar sesión para cambiar tu contraseña.";
      default:
        return error.toString();
    }
  }

  function updateUsuario(usuario, customMessage = "") {
    const promises = [];
    if (usuario.picture && usuario.picture !== null) {
      if (usuario.picture) {
        const promiseAdjunto = new Promise((resolve, reject) => {
          const formData = new FormData();
          formData.append("file", usuario.picture);
          AdjuntosService.postAdjunto(formData).then((res) => {
            const { file_id } = res.data;
            usuario.file_id = file_id;
            resolve();
          });
        });
        promises.push(promiseAdjunto);
      }
    }
    Promise.all(promises).then(() => {
      const data = { ...usuario };
      delete data.file;
      delete data.uid;
      delete data.activo;
      CustomerService.putCurrentCustomer(data)
        .then((res) => {
          dispatch({ type: GUARDAR_USUARIO });
          success(customMessage || "Perfil actualizado con éxito.");
          getUsuario();
        })
        .catch((error) => {
          alert(error);
        });
    });
  }

  const verifyEmail = () => {
    AuthService.verifyEmail().then(() => {
      success("Te enviamos un correo para confirmar tu dirección");
    });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        signIn,
        signUp,
        signOut,
        getUsuario,
        cancelEdit,
        verifyEmail,
        userLoggedIn,
        updateUsuario,
        editarUsuario,
        recoverPassword,
        updatePassword,
        setPropiedadUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
